// Brand marks live here, separate from Icon.tsx, because they intentionally use brand-fixed colors and are not subject to the tokens-only rule.
import React, { useId, useState } from 'react';

type Props = { size?: number };

/**
 * Canonical brand-hex constants for the social/email providers. These are the
 * single source of truth for provider brand colors — the carve-out from the
 * tokens-only rule lives here alongside the brand marks. Import these rather
 * than re-declaring the hex at a callsite (e.g. the connected-login accent in
 * `features/settings/rows.tsx`).
 */
export const PROVIDER_BRAND_COLOR = {
  google: '#4285F4',
  /** Google's brand red — used where Google must read apart from Microsoft's
   * blue (the warmup ESP split, whose two blues would be indistinguishable). */
  googleRed: '#EA4335',
  microsoft: '#0078D4',
} as const;

/* ---------- Gmail ----------
 * Classic 5-facet Gmail envelope mark, ported verbatim (same paths/colors)
 * from the old TrulyInbox webui's `shared/svg/gmail-icon.tsx` for visual
 * parity between the two apps' "connect an email account" catalogs.
 */
export function GmailIcon({ size = 16 }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 45 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M42.1875 14.8501L37.5 17.3709L32.8125 21.7251V36.6668H39.375C40.9284 36.6668 42.1875 35.4357 42.1875 33.9168V14.8501Z" fill="#4CAF50" />
      <path d="M2.8125 14.8501L6.20062 16.4176L12.1875 21.7251V36.6668H5.625C4.07156 36.6668 2.8125 35.4357 2.8125 33.9168V14.8501Z" fill="#1E88E5" />
      <path d="M32.8125 10.2666L22.5 17.8291L12.1875 10.2666L11.25 15.5833L12.1875 21.7249L22.5 29.2874L32.8125 21.7249L33.75 15.5833L32.8125 10.2666Z" fill="#E53935" />
      <path d="M2.8125 11.2733V14.8502L12.1875 21.7252V10.2668L9.25875 8.12091C8.56125 7.60941 7.71375 7.3335 6.84187 7.3335C4.61625 7.3335 2.8125 9.09716 2.8125 11.2733Z" fill="#C62828" />
      <path d="M42.1875 11.2733V14.8502L32.8125 21.7252V10.2668L35.7413 8.12091C36.4388 7.60941 37.2862 7.3335 38.1581 7.3335C40.3837 7.3335 42.1875 9.09716 42.1875 11.2733Z" fill="#FBC02D" />
    </svg>
  );
}

/* ---------- Google Workspace ----------
 * Stylised G-mark using Google's four brand colors. Approximation — not the
 * pixel-precise official vector — but unambiguously the Google "G" so it
 * reads distinctly from personal Gmail in a row.
 */
export function GoogleWorkspaceIcon({ size = 16 }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M21.6 12.23c0-.64-.06-1.25-.16-1.84H12v3.49h5.4a4.62 4.62 0 0 1-2 3.03v2.51h3.23c1.89-1.74 2.97-4.3 2.97-7.19Z" fill="#4285F4" />
      <path d="M12 22c2.7 0 4.96-.9 6.63-2.43l-3.23-2.51c-.9.6-2.04.96-3.4.96-2.62 0-4.84-1.77-5.63-4.15H3.04v2.6A10 10 0 0 0 12 22Z" fill="#34A853" />
      <path d="M6.37 13.87a6 6 0 0 1 0-3.74V7.53H3.04a10 10 0 0 0 0 8.94l3.33-2.6Z" fill="#FBBC04" />
      <path d="M12 5.96c1.47 0 2.79.5 3.83 1.5l2.87-2.87C16.95 2.99 14.7 2 12 2A10 10 0 0 0 3.04 7.53l3.33 2.6C7.16 7.73 9.38 5.96 12 5.96Z" fill="#EA4335" />
    </svg>
  );
}

/* ---------- Outlook ----------
 * Microsoft's official gradient "twisted ribbon" Outlook mark, ported from
 * the old TrulyInbox webui's `shared/svg/outlook-icon.tsx` for visual parity.
 * Gradient/clip-path ids are namespaced per-instance via `useId()` — this
 * component can render many times on one page (filter pills, table rows),
 * and the old repo's hardcoded ids would collide across instances.
 */
export function OutlookIcon({ size = 16 }: Props) {
  const uid = useId();
  const id = (name: string) => `outlook-${name}-${uid}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 35 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath={`url(#${id('clip')})`}>
        <path
          d="M14.0729 1.41406L13.876 1.53589C13.5661 1.72785 13.2708 1.94197 12.9937 2.17455L13.5625 1.77215H18.375L19.25 8.47991L14.875 12.9099L10.5 15.9888V19.5401C10.5 22.0209 11.7797 24.3209 13.876 25.6019L18.4807 28.426L8.75 34.1739H6.8724L3.37604 32.0291C1.27969 30.7444 0 28.4481 0 25.9673V14.0285C0 11.5477 1.27969 9.24409 3.37604 7.96308L13.876 1.5322C13.9417 1.49036 14.0073 1.45098 14.0729 1.41406Z"
          fill={`url(#${id('paint0')})`}
        />
        <path
          d="M14.0729 1.41406L13.876 1.53589C13.5661 1.72785 13.2708 1.94197 12.9937 2.17455L13.5625 1.77215H18.375L19.25 8.47991L14.875 12.9099L10.5 15.9888V19.5401C10.5 22.0209 11.7797 24.3209 13.876 25.6019L18.4807 28.426L8.75 34.1739H6.8724L3.37604 32.0291C1.27969 30.7444 0 28.4481 0 25.9673V14.0285C0 11.5477 1.27969 9.24409 3.37604 7.96308L13.876 1.5322C13.9417 1.49036 14.0073 1.45098 14.0729 1.41406Z"
          fill={`url(#${id('paint1')})`}
        />
        <path
          d="M34.1248 21.7704L34.9998 23.0994V25.9678C34.9998 28.4449 33.7201 30.7448 31.6238 32.0295L21.1238 38.4678C18.8962 39.8337 16.1034 39.8337 13.8758 38.4678L3.62012 32.1809C5.80397 33.4028 8.47637 33.3548 10.6238 32.0369L21.1238 25.6023C23.2201 24.3176 24.4998 22.0177 24.4998 19.5369V15.5684L34.1248 21.7704Z"
          fill={`url(#${id('paint2')})`}
        />
        <path
          d="M34.1248 21.7704L34.9998 23.0994V25.9678C34.9998 28.4449 33.7201 30.7448 31.6238 32.0295L21.1238 38.4678C18.8962 39.8337 16.1034 39.8337 13.8758 38.4678L3.62012 32.1809C5.80397 33.4028 8.47637 33.3548 10.6238 32.0369L21.1238 25.6023C23.2201 24.3176 24.4998 22.0177 24.4998 19.5369V15.5684L34.1248 21.7704Z"
          fill={`url(#${id('paint3')})`}
        />
        <path
          d="M35 14.0287V25.9675C35 26.0438 34.9988 26.1213 34.9964 26.2001C34.9198 23.8079 33.6547 21.6114 31.624 20.3673L21.124 13.9364C18.8964 12.5705 16.1036 12.5705 13.876 13.9364L10.5 16.0037V7.60148C10.5 5.12068 11.7797 2.82077 13.876 1.53607L14.0839 1.40687C16.2786 0.166466 18.9656 0.210766 21.124 1.53238L31.624 7.96327C33.7203 9.24428 35 11.5479 35 14.0287Z"
          fill={`url(#${id('paint4')})`}
        />
        <path
          d="M35 14.0287V25.9675C35 26.0438 34.9988 26.1213 34.9964 26.2001C34.9198 23.8079 33.6547 21.6114 31.624 20.3673L21.124 13.9364C18.8964 12.5705 16.1036 12.5705 13.876 13.9364L10.5 16.0037V7.60148C10.5 5.12068 11.7797 2.82077 13.876 1.53607L14.0839 1.40687C16.2786 0.166466 18.9656 0.210766 21.124 1.53238L31.624 7.96327C33.7203 9.24428 35 11.5479 35 14.0287Z"
          fill={`url(#${id('paint5')})`}
        />
      </g>
      <defs>
        <radialGradient id={id('paint0')} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(11.7396 8.15874) scale(29.1958 29.5629)">
          <stop offset="0.064" stopColor="#AE7FE2" />
          <stop offset="1" stopColor="#0078D4" />
        </radialGradient>
        <linearGradient id={id('paint1')} x1="11.8234" y1="32.2838" x2="7.57775" y2="25.035" gradientUnits="userSpaceOnUse">
          <stop stopColor="#114A8B" />
          <stop offset="1" stopColor="#0078D4" stopOpacity="0" />
        </linearGradient>
        <radialGradient id={id('paint2')} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(5.62533 30.9405) scale(27.1687 27.5103)">
          <stop offset="0.134" stopColor="#D59DFF" />
          <stop offset="1" stopColor="#5E438F" />
        </radialGradient>
        <linearGradient id={id('paint3')} x1="31.8133" y1="21.2166" x2="27.2718" y2="27.6459" gradientUnits="userSpaceOnUse">
          <stop stopColor="#493474" />
          <stop offset="1" stopColor="#8C66BA" stopOpacity="0" />
        </linearGradient>
        <radialGradient id={id('paint4')} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(32.4224 22.2168) scale(21.8057 22.0799)">
          <stop offset="0.058" stopColor="#50E6FF" />
          <stop offset="1" stopColor="#436DCD" />
        </radialGradient>
        <linearGradient id={id('paint5')} x1="11.3531" y1="1.44378" x2="17.9266" y2="1.44378" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2D3F80" />
          <stop offset="1" stopColor="#436DCD" stopOpacity="0" />
        </linearGradient>
        <clipPath id={id('clip')}>
          <rect width="35" height="40" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

/* ---------- Microsoft 365 ----------
 * Microsoft 4-square mark in its official 4 brand colors.
 */
export function Ms365Icon({ size = 16 }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="3" y="3" width="8.5" height="8.5" fill="#F25022" />
      <rect x="12.5" y="3" width="8.5" height="8.5" fill="#7FBA00" />
      <rect x="3" y="12.5" width="8.5" height="8.5" fill="#00A4EF" />
      <rect x="12.5" y="12.5" width="8.5" height="8.5" fill="#FFB900" />
    </svg>
  );
}

/* ---------- SMTP ----------
 * Neutral envelope — the one case where we DO use `currentColor` so the
 * fallback can inherit `var(--ink-500)` from its wrapper.
 */
export function SmtpIcon({ size = 16 }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color: 'var(--ink-500)' }}
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

/* ---------- ESP-brand logos ----------
 * The 8 cosmetic ESP-brand `EmailProvider` members (ZOHO/YAHOO/SENDGRID/
 * MAILGUN/AMAZON_SES/ELASTIC_EMAIL/YANDEX/GODADDY, see
 * apps/api/.../provider-presets.constants.ts) have no hand-drawn mark here —
 * they render the same `/logos/<id>.svg` asset the "connect an account"
 * catalog uses (`ProviderCatalogTile`'s `IconInner`), keyed by the identical
 * derivation the backend's `presetToEmailProvider` uses in reverse: lowercase,
 * `_` -> `-`. Falls back to the neutral envelope if the asset 404s, same
 * fallback shape as that catalog tile.
 */
function espBrandLogoSlug(key: string): string | undefined {
  return ESP_BRAND_LOGO_KEYS.has(key) ? key.toLowerCase().replace(/_/g, '-') : undefined;
}

const ESP_BRAND_LOGO_KEYS = new Set([
  'ZOHO',
  'YAHOO',
  'SENDGRID',
  'MAILGUN',
  'AMAZON_SES',
  'ELASTIC_EMAIL',
  'YANDEX',
  'GODADDY',
]);

function EspBrandIcon({ slug, size = 16 }: Props & { slug: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <SmtpIcon size={size} />;
  return (
    <img
      src={`/logos/${slug}.svg`}
      alt=""
      width={size}
      height={size}
      className="shrink-0 object-contain"
      onError={() => setFailed(true)}
    />
  );
}

export function ProviderIcon({
  provider,
  size = 16,
}: {
  provider: string;
  size?: number;
}): React.ReactElement {
  const key = provider.toUpperCase();
  switch (key) {
    case 'GMAIL':
      return <GmailIcon size={size} />;
    case 'GOOGLE_WORKSPACE':
      return <GoogleWorkspaceIcon size={size} />;
    case 'OUTLOOK':
      return <OutlookIcon size={size} />;
    case 'GOOGLE_WORKSPACE_DIRECTORY':
      return <EspBrandIcon slug="google-workspace-directory" size={size} />;
    case 'MS365':
    case 'AZURE':
      return <Ms365Icon size={size} />;
    case 'SMTP':
    case 'OTHERS':
      return <SmtpIcon size={size} />;
    default: {
      const slug = espBrandLogoSlug(key);
      if (slug) return <EspBrandIcon slug={slug} size={size} />;
      return <SmtpIcon size={size} />;
    }
  }
}
